/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../nls/StringResources',
	'react-dom',
	'react',
	'@ba/ba-ui-carbon-toolkit',
	'corsair_ship/ba-react-html5-dnd'
], function (StringResources, ReactDOM, React, UI_CARBON_TOOLKIT, ReactHtml5DnDContext) {
	'use strict';

	/* eslint react/prop-types: 0 */ // TODO: Remove once we have prop-types brought in from glass.
	const { SVGIcon,
		Container,
		FlexLayout,
		FlexItem,
		SimpleTable,
		TruncatedText,
		classnames } = UI_CARBON_TOOLKIT;

	class SortableList extends React.Component {

		constructor(props) {
			super(props);
			this.state = {
				data: props.sortableData || []
			};
			this.idPrefix = props.idPrefix || '';
			this.resources = {
				moveToTop: StringResources.get('moveToTop'),
				clear: StringResources.get('clear'),
				createCustomSort: StringResources.get('createCustomSort'),
				selectAvailableItemsDesc: StringResources.get('selectAvailableItemsDesc')
			};
			this.onChange = props.onChange;
			this.iconsFeature = props.iconsFeature;
		}

		static getDerivedStateFromProps(props) {
			return {
				data: props.sortableData
			};
		}

		/**
		 * Reposition an item in the list
		 * @param {array} arr list of sorted items
		 * @param {int} from original index of the item to be moved
		 * @param {int} to final index of the item to be moved
		 */
		_reOrderRow(arr, from, to) {
			const objData = [...arr];
			objData.splice(to, 0, objData.splice(from, 1)[0]);
			this.onChange(objData);
		}

		/**
		 * Move the item at the selected row to the top of the list
		 * @param {int} row index of item to be moved
		 */
		_moveToTop(row) {
			if (row !== 0) {
				this._reOrderRow(this.state.data, row, 0);
			}
		}

		/**
		 * Remove an item from the list
		 * @param {string} value 'value' string of the item to remove
		 * @param {object} event click/keypress event
		 */
		_removeItem(value, evt) {
			if (evt) {
				evt.preventDefault();
				evt.stopPropagation();
			}
			const filteredData = this.state.data.filter(
				dataItem =>
					value !== dataItem.value
			);
			this.onChange(filteredData, value);
		}

		/**
		 * Render a table cell (note since each row only has one cell 'col' value is irrelevant)
		 * @param {int} row index of table cell being rendered
		 */
		_cellRenderer(row) {
			const itemValue = this.state.data[row].value;
			const itemLabel = this.state.data[row].label;
			const draggableIcon = this.iconsFeature.getIcon('draggable_32');
			const removeIcon = this.iconsFeature.getIcon('remove');
			const upToTopIcon = this.iconsFeature.getIcon('upToTop');

			return (
				<FlexLayout
					className='ba-sortableList__content'
					gutter={[1, 1]}
					id={this.idPrefix + itemValue}
					onClose={this._closeContainer}
				>
					<FlexLayout
						className='ba-sortableList__resize'
						justifyContent='center'
						alignItems='center'
					>
						<SVGIcon focusable={false} iconId={draggableIcon.id} />
					</FlexLayout>
					<Container
						className='ba-sortableList__label'
						padding={[1, 1, 1, 0]}
					>
						<TruncatedText
							value={itemLabel}
							location='end'
						/>
					</Container>
					<Container
						className='ba-sortableList__buttons'
						gutter={[1, 1]}
					>
						<FlexItem
							className={classnames({
								'ba-sortableList__moveToTop': true,
								'ba-sortableList__moveToTop__hidden': (row === 0)
							})}
							justifyContent='center'
							alignItems='center'
							tabIndex={(row > 0) ? 0 : null}
							aria-label={this.resources['moveToTop'] + ' ' + itemLabel}
							role={'link'}
							onClick={() => this._moveToTop(row)}
							onKeyPress={(evt) => {
								if (evt.key === 'Enter') {
									this._moveToTop(row);
								}
							}}
						>
							<SVGIcon iconId={upToTopIcon.id} />
						</FlexItem>
						<FlexItem
							className='ba-sortableList__remove'
							justifyContent='center'
							alignItems='center'
							aria-label={this.resources['clear'] + ' ' + itemLabel}
							role={'link'}
							tabIndex={0}
							onClick={(evt) => this._removeItem(itemValue, evt)}
							onKeyPress={(evt) => {
								if (evt.key === 'Enter') {
									this._removeItem(itemValue, evt);
								}
							}}
						>
							<SVGIcon
								iconId={removeIcon.id}
							/>
						</FlexItem>
					</Container>
				</FlexLayout>
			);
		}

		render() {
			const output = (this.state.data.length > 0) ? (
				<div
					className='ba-sortableList__container'
				>
					<SimpleTable
						colToAddScopeRow={1}
						draggable={true}
						height={'100%'}
						rowLength={this.state.data.length}
						colLength={1}
						cellRenderer={this._cellRenderer.bind(this)}
						onRowMove={(from, to) => {
							this._reOrderRow(this.state.data, from, to);
						}}
					/>
				</div>
			) : (
				<div
					className='ba-sortableList__emptyView'
				>
					<div
						className='ba-sortableList__emptyView__text__line1'
						role='option'
					>
						{this.resources['createCustomSort']}
					</div>
					<div
						className='ba-sortableList__emptyView__text__line2'
						role='option'
					>
						{this.resources['selectAvailableItemsDesc']}
					</div>
				</div>
			);
			/* workaround for issue that focus 'bleeds' into new render, e.g. when 'Move to top'/'Clear' links are clicked and a re-render places
			   a different item at that same position the corresponding link of the new item will be in focus */
			this._blurLinkElements();
			return output;
		}

		/**
		 * Blur all link elements in the sortable list
		 */
		_blurLinkElements() {
			const container = document.getElementsByClassName('ba-sortableList__container');
			if (container && container[0]) {
				const els = container[0].querySelectorAll('.ba-sortableList__moveToTop, .ba-sortableList__remove');
				if (els && els.length > 0) {
					for (var i = 0; i < els.length; i++) {
						els[i].blur();
					}
				}
			}
		}
	}

	return ReactHtml5DnDContext(SortableList);
});
